import { Component, Input, OnChanges, SimpleChanges, OnDestroy, Output, EventEmitter } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Sort } from '@angular/material/sort';
import { GroupsService } from '../services/groups.service';
import { filter, flatMap, tap, takeUntil, concatAll, takeLast, map } from 'rxjs/operators';
import { TopicSelectorDialogComponent } from '../settings/topicSelectorDialog.component';
import * as _ from 'lodash';
@Component({
    selector: 'group-topics',
    styles: [`
        .topicsPanel {
            overflow-y: auto;
            height: calc(100vh - 225px);
        }
        .cdk-column-published.mat-column-published,
        .cdk-column-followersCount.mat-column-followersCount,
        .cdk-column-itemsCount.mat-column-itemsCount,
        .cdk-column-actions.mat-column-actions {
            text-align: center;
        }
    `],
    template: `
        <mat-toolbar *ngIf="!displayOnly">
            <span fxFlex></span>
            <button mat-button color="primary" (click)="addSubscriptions()">Add Topics</button>
        </mat-toolbar>
        <div class="topicsPanel"
            infiniteScroll 
            [infiniteScrollThrottle]="300"
            [infiniteScrollDistance]="1"
            [scrollWindow]="false"
            (scrolled)="nextPage()">
            <table mat-table
                matSort 
                (matSortChange)="sort($event)"
                [matSortDisableClear]="true "
                [matSortActive]="params?.value?.sort" 
                [matSortDirection]="params?.value?.sortDirection" 
                [dataSource]="topics" 
                style="width: 100%;">
                <ng-container matColumnDef="title">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="titleLower">Title</th>
                    <td mat-cell *matCellDef="let row">
                        <a *ngIf="row.type === 'COLLECTION'" [routerLink]="['/topics', row.id, 'groups']">{{row.title}}</a>
                        <a *ngIf="row.type !== 'COLLECTION'" [routerLink]="['/settings/sources', row.id, 'groups']">{{row.title}}</a>
                    </td>
                </ng-container>
                <ng-container matColumnDef="published">
                    <th mat-header-cell *matHeaderCellDef width="75px">Published</th>
                    <td mat-cell *matCellDef="let row"><mat-icon *ngIf="row.published">check_circle_outline</mat-icon></td>
                </ng-container>
                <ng-container matColumnDef="followersCount">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="followersCount" width="75px">Followers</th>
                    <td mat-cell *matCellDef="let row">{{row.followersCount}}</td>
                </ng-container>
                <ng-container matColumnDef="itemsCount">
                    <th mat-header-cell *matHeaderCellDef mat-sort-header="itemsCount" width="75px">Items</th>
                    <td mat-cell *matCellDef="let row">{{ row.itemsCount }}</td>
                </ng-container>
                <ng-container matColumnDef="actions">
                    <th mat-header-cell *matHeaderCellDef width="75px"></th>
                    <td mat-cell *matCellDef="let row">
                        <button mat-icon-button (click)="removeTopic(row)" style="float: right;">
                            <i class="material-icons">clear</i>
                        </button>
                    </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="columns"></tr>
                <tr mat-row *matRowDef="let row; columns: columns;"></tr>
            </table>
            <div *ngIf="noTouchingPaging" fxLayoutAlign="center center">
                <spinner></spinner>
            </div>
            <div *ngIf="topics | async as results">
                <div *ngIf="!results.length">
                    <h5 style="text-align: center">
                        <p>No topics have been selected yet for this Group.</p>
                        <p>Choose "Add Topics" to select desired topics.</p>
                    </h5>
                </div>
            </div>
        </div>
`
})
export class GroupTopicsComponent implements OnChanges, OnDestroy {
    private cleanupTime = new Subject<void>();
    @Input() group;
    @Input() displayOnly: boolean;
    @Output() subscriptionsChanged = new EventEmitter<void>();
    private baseColumns = ['title', 'published', 'followersCount', 'itemsCount'];
    columns = this.baseColumns.concat(['actions']);
    params = new BehaviorSubject<any>({ 
        sort: 'firstNameLower',
        sortDirection: 'asc',
        page: 0, 
        rows: 40
    });
    topics = new BehaviorSubject<any[]>([]);
    lastFetched: boolean;
    noTouchingPaging: boolean;

    constructor(private snackBar: MatSnackBar,
                private dialog: MatDialog, 
                private groupsService: GroupsService) {
        this.params
        .pipe(
            filter(params => params.groupId),
            flatMap(params => this.groupsService.getTopics(params)),
            tap(response => this.lastFetched = response.last),
            tap(() => this.noTouchingPaging = false),
            takeUntil(this.cleanupTime)
        ).subscribe(response => this.topics.next(this.topics.value.concat(response.content)));
    }

    removeTopic(topic) {
        this.groupsService.removeTopic(this.group.id, topic.id)
        .subscribe(
            () => {
                this.snackBar.open(`${topic.title} has been removed from ${this.group.name}.`, 'OK', { duration: 3000});
                this.subscriptionsChanged.emit();
                this.refetch();
            },
            () => this.snackBar.open(`Unable to remove ${topic.title} please try again.`, 'OK', { duration: 3000})
        ) 
    }

    nextPage() {
        const params = this.params.value;
        params.page = params.page + 1;
        this.noTouchingPaging = true;
        this.params.next(params);
    }

    sort(sort: Sort) {
        const params = this.params.value;
        params.page = 0;
        params.sort = sort.active;
        params.sortDirection = sort.direction;
        this.noTouchingPaging = true;
        this.topics.next([]);
        this.params.next(params);        
    }

    private refetch() {
        this.noTouchingPaging = true;
        this.topics.next([]);
        this.params.next(Object.assign({}, this.params.value, { page: 0 }));
    }

    addSubscriptions() {
        let selectedTopics;
        const currentTopics = this.group.subscriptions.map(sub => sub.streamId); 
        this.dialog.open(TopicSelectorDialogComponent, { data: { currentTopics }}).afterClosed()
        .pipe(
            filter(res => !!res),
            map(topics => {
                selectedTopics = topics.filter(topic => !currentTopics.includes(topic.id))
                return selectedTopics;
            }),
            flatMap(topics => topics.map(topic => this.groupsService.addToStream(this.group.id, topic.id))),
            concatAll(),
            takeLast(1),
            tap(() => this.subscriptionsChanged.emit()),
        ).subscribe(() => {
            this.snackBar.open(`${this.group.name} subscribed to ${selectedTopics.length} additional topic${selectedTopics.length > 1 ? 's' : ''}.`, 'OK', { duration: 3000});
            this.refetch();
        });
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.displayOnly) {
            this.columns = changes.displayOnly.currentValue ? this.baseColumns : this.baseColumns.concat(['actions']);   
        }
        if (changes.group && changes.group.currentValue && !this.params.value.groupId) {
            this.params.next(Object.assign({}, this.params.value, { groupId: this.group.id }));
        }
    }

    ngOnDestroy(): void {
        this.cleanupTime.next();
        this.cleanupTime.complete();
    }
}
